import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Label,
  Input,
  CardHeader,
  Button,
  Badge,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import Flatpickr from "react-flatpickr";
import {
  getContractByIdFull as onGetContractByIdFull,
  updateContract as onUpdateContract,
} from "../../slices/thunks";
import moment from "moment";
import { ToastContainer } from "react-toastify";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import ContractUnits from "./ContractUnits";
import DocumentList from "./DocumentList";
import { createSelector } from "reselect";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const selectContractState = (state) => state.Contract;
const selectContractProperties = createSelector(selectContractState, (contract) => ({
  selectedContractFull: contract.selectedContractFull,
  contract: contract.contract,
  loading: contract.loading,
  error: contract.error,
}));

const ContractDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { selectedContractFull, contract, loading, error} = useSelector(selectContractProperties)

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (!id) return;
    dispatch(onGetContractByIdFull(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (contract) {
      setFormData(contract);
    }
  }, [contract]);

  const toggleEdit = () => {
    if (isEditing) {
      setFormData(contract || {});
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      await dispatch(onUpdateContract(formData)).unwrap();
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating contract:", error);
    }
  };

  const getClientTitle = () => {
    if (!selectedContractFull?.client) return "Loading...";

    const client = selectedContractFull.client;
    return `${client.name || ""} ${client.surname || ""}`.trim();
  };

  const handleClientClick = () => {
    if (selectedContractFull?.client?.id) {
      navigate(`/client-details/${selectedContractFull.client.id}`);
    }
  };

  const getPercentageCashed = () => {
    const total = selectedContractFull?.total_contract_value || 0;
    const cashed = selectedContractFull?.total_cashed || 0;
    if (total === 0) return 0;
    return Math.round((cashed / total) * 100);
  };

  if (loading && !selectedContractFull) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <div
              className="spinner-grow"
              style={{ color: "#4b38b3", width: "3rem", height: "3rem" }}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 fs-5">Loading contract details...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error && !selectedContractFull) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <div
              className="alert alert-danger p-4 shadow-sm border-0"
              style={{ borderRadius: "12px" }}
            >
              <i className="ri-error-warning-line fs-1 d-block mb-3"></i>
              <h4 className="mb-3">Failed to load contract details</h4>
              <p className="mb-4">{error}</p>
              <Button
                style={{
                  backgroundColor: "#4b38b3",
                  borderColor: "#4b38b3",
                  padding: "10px 24px",
                  borderRadius: "8px",
                }}
                onClick={() => dispatch(onGetContractByIdFull(id))}
              >
                <i className="ri-refresh-line me-1"></i> Retry
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const percentageCashed = getPercentageCashed();
  const primaryColor = "#4b38b3";
  const secondaryPurple = "#6852ed";

  return (
    <div className="page-content">
      <ToastContainer />
      <Container fluid>
        <BreadCrumb
          pageTitle="Contracts"
          link={location?.state || "/"}
        />
        <div
          className="header-card mb-4 p-4 border-0 shadow position-relative"
          style={{
            borderRadius: "16px",
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryPurple} 100%)`,
          }}
        >
          <Row className="align-items-center">
            <Col lg={7}>
              <div className="text-white">
                <div className="d-flex align-items-center mb-3">
                  <div className="me-3">
                    <div
                      className="rounded-circle bg-white bg-opacity-20 d-flex align-items-center justify-content-center"
                      style={{ width: "56px", height: "56px" }}
                    >
                      <span className="fs-3 text-white">📄</span>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex align-items-center">
                      <h3 className="mb-0 me-3 text-white-75">
                        Contract #{selectedContractFull?.pvc_number}
                      </h3>
                      <Badge
                        pill
                        className="px-3 py-2 fs-7"
                        style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
                      >
                        {selectedContractFull?.type}
                      </Badge>
                    </div>
                    <p className="mb-0 mt-1 opacity-75">
                      <i className="ri-calendar-line me-1"></i>
                      {moment(selectedContractFull?.pvc_date).format(
                        "MMMM D, YYYY"
                      )}
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center mt-3">
                  <Button
                    color="light"
                    className="text-purple d-flex align-items-center me-3 shadow-sm"
                    style={{ borderRadius: "8px" }}
                    onClick={handleClientClick}
                  >
                    <i className="ri-user-line me-1"></i> {getClientTitle()} <i className="ri-external-link-line" style={{ marginLeft: '8px' }}/>
                  </Button>
                  <div className="text-end text-white-75">
                    <div className="d-flex align-items-center justify-content-center">
                      <span className="me-2 fs-5 fw-bold">
                        {selectedContractFull?.units?.length || 0}
                      </span>
                      <span>Units</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={5} className="mt-4 mt-lg-0">
              <div className="glass-card p-4 text-white">
                <Row className="text-center mb-3">
                  <Col xs={6}>
                    <p className="text-white-50 mb-1">Total Value</p>
                    <h3 className="mb-0 text-white-75">
                      €
                      {new Intl.NumberFormat("en-US").format(
                        selectedContractFull?.total_contract_value || 0
                      )}
                    </h3>
                  </Col>
                  <Col xs={6}>
                    <p className="text-white-50 mb-1">Total Cashed</p>
                    <h3 className="mb-0 text-white-75">
                      €
                      {new Intl.NumberFormat("en-US").format(
                        selectedContractFull?.total_cashed || 0
                      )}
                    </h3>
                  </Col>
                </Row>
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-white-50">Payment Progress</small>
                    <small className="badge bg-white text-purple fw-normal">
                      {percentageCashed}%
                    </small>
                  </div>
                  <div
                    className="progress"
                    style={{
                      height: "14px",
                      borderRadius: "7px",
                      backgroundColor: "rgba(255,255,255,0.25)",
                    }}
                  >
                    <div
                      className="progress-bar"
                      style={{
                        width: `${percentageCashed}%`,
                        borderRadius: "7px",
                        background: "linear-gradient(45deg, #0ab39c, #2ce59b)",
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <Row className="g-4">
          <Col lg={8}>
            <Card
              className="mb-4 border-0 shadow-lg card-hover"
              style={{ borderRadius: "12px", transition: "all 0.3s ease" }}
            >
              <CardHeader className="d-flex justify-content-between align-items-center bg-transparent border-bottom">
                <div className="d-flex align-items-center">
                  <span
                    className="text-white rounded-circle p-2 me-2 shadow-sm"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <i className="ri-information-line fs-5"></i>
                  </span>
                  <h5 className="mb-0">Contract Details</h5>
                </div>
                <div>
                  <Button
                    color={isEditing ? "danger" : "primary"}
                    style={
                      isEditing
                        ? {}
                        : {
                            backgroundColor: primaryColor,
                            borderColor: primaryColor,
                          }
                    }
                    className={`me-2 px-3 py-2 shadow-sm ${
                      isEditing ? "" : "btn-glow"
                    }`}
                    onClick={toggleEdit}
                  >
                    {isEditing ? (
                      <>
                        <i className="ri-close-line me-1"></i> Cancel
                      </>
                    ) : (
                      <>
                        <i className="ri-edit-line me-1"></i> Edit
                      </>
                    )}
                  </Button>
                  {isEditing && (
                    <Button
                      color="success"
                      onClick={handleSave}
                      className="px-3 py-2 shadow-sm btn-glow"
                      style={{ borderRadius: "8px" }}
                    >
                      <i className="ri-check-line me-1"></i> Save
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardBody className="p-4">
                <Row className="g-3">
                  <Col md={6}>
                    <div className="form-floating mb-3">
                      <Input
                        type="text"
                        id="pvc_number"
                        name="pvc_number"
                        placeholder="PVC Number"
                        className="form-control-lg shadow-sm"
                        style={{
                          borderRadius: "8px",
                          border: isEditing
                            ? "1px solid #ced4da"
                            : "1px solid transparent",
                          backgroundColor: "#f8f9fa",
                        }}
                        value={formData?.pvc_number || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                      <Label for="pvc_number">PVC Number</Label>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-floating mb-3">
                      <Flatpickr
                        className="form-control form-control-lg shadow-sm"
                        style={{
                          borderRadius: "8px",
                          border: isEditing
                            ? "1px solid #ced4da"
                            : "1px solid transparent",
                          backgroundColor: "#f8f9fa",
                        }}
                        id="pvc_date"
                        name="pvc_date"
                        placeholder="Enter Date"
                        options={{
                          enableTime: false,
                          altInput: true,
                          altFormat: "d M, Y",
                          dateFormat: "Y-m-d",
                        }}
                        value={formData?.pvc_date || ""}
                        onChange={(date) => {
                          if (isEditing && date.length > 0) {
                            setFormData((prevData) => ({
                              ...prevData,
                              pvc_date: moment(date[0]).format("YYYY-MM-DD"),
                            }));
                          }
                        }}
                        ref={(flatpickr) => {
                          if (flatpickr && flatpickr.flatpickr) {
                            const fpInstance = flatpickr.flatpickr;
                            if (!isEditing) {
                              fpInstance.set("clickOpens", false);
                              fpInstance.altInput.disabled = true;
                              fpInstance.input.disabled = true;
                            } else {
                              fpInstance.set("clickOpens", true);
                              fpInstance.altInput.disabled = false;
                              fpInstance.input.disabled = false;
                            }
                          }
                        }}
                      />
                      <Label for="pvc_date">PVC Date</Label>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-floating mb-3">
                      <Input
                        type="text"
                        id="notary"
                        name="notary"
                        placeholder="Notary"
                        className="form-control-lg shadow-sm"
                        style={{
                          borderRadius: "8px",
                          border: isEditing
                            ? "1px solid #ced4da"
                            : "1px solid transparent",
                          backgroundColor: "#f8f9fa",
                        }}
                        value={formData?.notary || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                      <Label for="notary">Notary</Label>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-floating mb-3">
                      <Input
                        name="type"
                        id="type"
                        className="form-control-lg shadow-sm"
                        style={{
                          borderRadius: "8px",
                          border: isEditing
                            ? "1px solid #ced4da"
                            : "1px solid transparent",
                          backgroundColor: "#f8f9fa",
                        }}
                        onChange={handleInputChange}
                        placeholder="Select Contract Type"
                        type="select"
                        value={formData?.type || ""}
                        disabled={!isEditing}
                      >
                        <option value="">Select Type</option>
                        <option value="PRE-CONTRACT">Pre-Contract</option>
                        <option value="CREDIT">Credit</option>
                        <option value="CASH">Cash</option>
                        <option value="DEVELOPER">Developer</option>
                        <option value="OTHER">Other</option>
                      </Input>
                      <Label for="type">Type</Label>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="form-floating mb-0">
                      <Input
                        type="textarea"
                        id="notes"
                        name="notes"
                        placeholder="Notes"
                        style={{
                          height: "100px",
                          borderRadius: "8px",
                          border: isEditing
                            ? "1px solid #ced4da"
                            : "1px solid transparent",
                          backgroundColor: "#f8f9fa",
                        }}
                        className="shadow-sm"
                        value={formData?.notes || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                      <Label for="notes">Notes</Label>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card
              className="mb-4 border-0 shadow-lg card-hover"
              style={{ borderRadius: "12px", transition: "all 0.3s ease" }}
            >
              <CardHeader className="bg-transparent border-bottom">
                <div className="d-flex align-items-center">
                  <span
                    className="text-white rounded-circle p-2 me-2 shadow-sm"
                    style={{ backgroundColor: "#4b38b3" }}
                  >
                    <i className="ri-building-line fs-5"></i>
                  </span>
                  <h5 className="mb-0">
                    Units{" "}
                    <span
                      className="badge rounded-pill ms-2"
                      style={{ backgroundColor: "#4b38b3" }}
                    >
                      {selectedContractFull?.units?.length || 0}
                    </span>
                  </h5>
                </div>
              </CardHeader>
              <CardBody className="p-4">
                <ContractUnits units={selectedContractFull?.units || []} />
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card
              className="border-0 shadow-lg h-100 card-hover"
              style={{ borderRadius: "12px", transition: "all 0.3s ease" }}
            >
              <CardHeader className="bg-transparent border-bottom">
                <div className="d-flex align-items-center">
                  <span
                    className="text-white rounded-circle p-2 me-2 shadow-sm"
                    style={{ backgroundColor: "#4b38b3" }}
                  >
                    <i className="ri-file-list-line fs-5"></i>
                  </span>
                  <h5 className="mb-0">Documents</h5>
                </div>
              </CardHeader>
              <CardBody className="p-4">
                <DocumentList contract={selectedContractFull} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {isEditing && (
        <div
          className="position-fixed"
          style={{
            bottom: "2rem",
            right: "2rem",
            zIndex: 1050,
            animation: "fadeIn 0.3s ease-in-out",
          }}
        >
          <Button
            color="success"
            className="btn-lg rounded-circle shadow-lg btn-glow"
            onClick={handleSave}
            style={{ width: "64px", height: "64px" }}
          >
            <i className="ri-save-line fs-4"></i>
          </Button>
        </div>
      )}

      <style>
        {`
          .text-purple {
            color: ${primaryColor} !important;
          }
          
          .bg-purple {
            background-color: ${primaryColor} !important;
          }
          
          .card-hover:hover {
            transform: translateY(-5px);
            box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175) !important;
          }
          
          .glass-card {
            background-color: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }
          
          .text-white-75 {
            color: rgba(255, 255, 255, 0.75) !important;
          }
          
          .text-white-50 {
            color: rgba(255, 255, 255, 0.50) !important;
          }

          
          .shadow-lg {
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
          }
          
          .form-floating > .form-control-lg {
            height: calc(3.5rem + 2px);
            line-height: 1.25;
            border-radius: 8px;
          }
          
          .form-floating > label {
            padding: 1rem 0.75rem;
            opacity: 0.75;
          }
          
          .form-control:disabled {
            background-color: #f8f9fa !important;
            opacity: 0.75;
          }
          
          .rounded-circle {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
          }
          
          .breadcrumb-item.active {
            color: ${primaryColor};
          }
          
          .bg-opacity-20 {
            opacity: 0.2;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          .bg-white.text-purple {
            color: ${primaryColor} !important;
          }
          
          .fs-7 {
            font-size: 0.875rem !important;
          }
          
          @keyframes glow {
            0% {
              box-shadow: 0 0 5px rgba(75, 56, 179, 0.4);
            }
            50% {
              box-shadow: 0 0 20px rgba(75, 56, 179, 0.6);
            }
            100% {
              box-shadow: 0 0 5px rgba(75, 56, 179, 0.4);
            }
          }
          
          .btn-glow {
            animation: glow 1.5s infinite;
          }
          
          .header-card {
            position: relative;
            overflow: hidden;
          }
          
          .header-card::before {
            content: "";
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 60%);
            transform: rotate(30deg);
          }
        `}
      </style>
    </div>
  );
};

export default ContractDetail;
